import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { API } from '../../../core/API';
import { CodificationsService } from '../../../core/service/codifications-service/codifications.service';
import { ApiService } from '../../../core/service/api-service/api-service';

export type TerminalLine =
  | { kind: 'info';    text: string }
  | { kind: 'success'; text: string; licence: string }
  | { kind: 'error';   text: string; demande_id: number }
  | { kind: 'done';    generated: number; failed: number; total: number };

@Component({
  selector: 'app-generate-all-terminal',
  standalone: false,
  templateUrl: './generate-all-terminal.component.html',
})
export class GenerateAllTerminalComponent implements OnInit, OnDestroy {
  @Input() entityType!: string;
  @Input() entityLabel = 'licences';
  @Input() mode: 'generate' | 'regenerate' = 'generate';
  @Input() streamPath?: string;   // overrides the mode-derived SSE path (full path incl. query)
  @Input() cancelPath?: string;   // overrides the mode-derived cancel path
  @Input() itemNoun = 'Demande';  // label for each processed item (e.g. 'Licence')
  @Output() closed = new EventEmitter<boolean>();

  private codificationsService = inject(CodificationsService);
  private api = inject(ApiService);

  lines = signal<TerminalLine[]>([]);
  isDone = signal(false);
  isRunning = signal(false);
  isConfirming = signal(true);
  isStopping = signal(false);
  fedValidatedLabel = signal('Validée par la fédération');
  totalCount = signal(0);
  processedCount = signal(0);
  progressPct = computed(() => this.totalCount() > 0 ? Math.round((this.processedCount() / this.totalCount()) * 100) : 0);

  private es: EventSource | null = null;

  ngOnInit(): void {
    this.codificationsService.getCodificationsByGroup('DEMANDE_STATUS').subscribe({
      next: (r) => {
        const entry = (r.data || []).find((c: any) => c.codification_code === 'FED_VALIDATED');
        if (entry?.codification_label) this.fedValidatedLabel.set(entry.codification_label);
      },
    });
  }

  confirm(): void {
    this.isConfirming.set(false);
    this.start();
  }

  generatedCount = computed(() => {
    const last = [...this.lines()].reverse().find(l => l.kind === 'done' || l.kind === 'success' || l.kind === 'error');
    if (!last) return 0;
    if (last.kind === 'done') return (last as any).generated;
    const done = this.lines().find(l => l.kind === 'done');
    return done ? (done as any).generated : 0;
  });

  get isRegenerateMode(): boolean { return this.mode === 'regenerate'; }

  start(): void {
    if (this.isRunning()) return;
    this.lines.set([]);
    this.isDone.set(false);
    this.isRunning.set(true);

    const streamPath = this.streamPath
      ?? (this.isRegenerateMode
        ? API.LICENCES.REGENERATE_ALL_STREAM(this.entityType)
        : API.LICENCES.GENERATE_ALL_STREAM(this.entityType));
    const url = `${environment.apiUrl}${streamPath}`;
    this.es = new EventSource(url, { withCredentials: true });

    this.es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleEvent(data);
      } catch { /* ignore parse errors */ }
    };

    this.es.onerror = () => {
      this.push({ kind: 'error', text: 'Connexion perdue', demande_id: 0 });
      this.finish();
    };
  }

  private handleEvent(data: any): void {
    switch (data.type) {
      case 'start':
        this.totalCount.set(data.total);
        this.processedCount.set(0);
        this.push({ kind: 'info', text: `Démarrage — ${data.total} ${this.itemNoun.toLowerCase()}(s) à traiter` });
        break;
      case 'success':
        this.processedCount.set(data.generated + data.failed);
        this.push({ kind: 'success', text: data.label || `${this.itemNoun} #${data.demande_id}`, licence: data.licence_number });
        break;
      case 'error':
        this.processedCount.set(data.generated + data.failed);
        this.push({ kind: 'error', text: `${data.label || this.itemNoun + ' #' + data.demande_id} — ${data.error}`, demande_id: data.demande_id });
        break;
      case 'done':
        this.push({ kind: 'done', generated: data.generated, failed: data.failed, total: data.total });
        this.finish();
        break;
      case 'cancelled':
        this.push({ kind: 'done', generated: data.generated, failed: data.failed, total: data.total });
        this.finish();
        break;
      case 'fatal':
        this.push({ kind: 'error', text: data.error, demande_id: 0 });
        this.finish();
        break;
    }
  }

  private push(line: TerminalLine): void {
    this.lines.update(lines => [...lines, line]);
    // scroll to bottom after render
    setTimeout(() => {
      const el = document.getElementById('terminal-body');
      if (el) el.scrollTop = el.scrollHeight;
    }, 0);
  }

  private finish(): void {
    this.es?.close();
    this.es = null;
    this.isRunning.set(false);
    this.isStopping.set(false);
    this.isDone.set(true);
  }

  stop(): void {
    if (!this.isRunning() || this.isStopping()) return;
    this.isStopping.set(true);
    const cancelUrl = this.cancelPath ?? (this.isRegenerateMode ? API.LICENCES.CANCEL_REGENERATE_STREAM : API.LICENCES.CANCEL_STREAM);
    this.api.post(cancelUrl, { entity_type: this.entityType }).subscribe({
      next: () => {},
      error: () => this.isStopping.set(false),
    });
  }

  downloadLogs(): void {
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    const lines = this.lines().map(l => {
      if (l.kind === 'info')    return `[INFO]    ${l.text}`;
      if (l.kind === 'success') return `[SUCCESS] ${l.text} → ${l.licence}`;
      if (l.kind === 'error')   return `[ERROR]   ${l.text}`;
      if (l.kind === 'done')    return `[DONE]    ${l.generated} générée(s) · ${l.failed} échouée(s) / ${l.total} total`;
      return '';
    });
    const action = this.isRegenerateMode ? 'Régénération' : 'Génération';
    const content = [`SportIQ — ${action} licences ${this.entityLabel}`, `Date: ${now}`, '', ...lines].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const prefix = this.isRegenerateMode ? 'regen' : 'licences';
    a.download = `${prefix}-${this.entityType}-${now.replace(/[: ]/g, '-')}.log`;
    a.click();
    URL.revokeObjectURL(url);
  }

  close(): void {
    this.finish();
    const hadGenerated = this.lines().some(l => l.kind === 'success');
    this.closed.emit(hadGenerated);
  }

  ngOnDestroy(): void {
    this.es?.close();
  }
}
